import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ShieldAlert, ShieldCheck, ShieldX, Eye } from "lucide-react";
import { useModReviewQueue, type ModReviewStatus } from "@/lib/moderation/useModReviewQueue";
import { toast } from "sonner";

const SOURCE_LABELS: Record<string, string> = {
  report: "Member report",
  behavioral_risk: "Behavioral risk",
  content_scan: "Content scan",
  manual: "Manual",
};

const Queue = ({ status }: { status: ModReviewStatus }) => {
  const { items, loading, error, resolve } = useModReviewQueue(status);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);

  if (loading) return <p className="text-sm text-muted-foreground py-4">Loading…</p>;
  if (error) return <p className="text-sm text-destructive py-4">{error}</p>;
  if (items.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-border p-8 text-center">
        <ShieldCheck className="mx-auto h-8 w-8 text-muted-foreground" />
        <p className="mt-2 text-sm text-muted-foreground">Nothing to review.</p>
      </div>
    );
  }

  const act = async (id: string, next: ModReviewStatus) => {
    setBusy(id);
    const { error } = await resolve(id, next, notes[id]?.trim() || undefined);
    setBusy(null);
    if (error) toast.error(error);
    else toast.success("Resolved.");
  };

  return (
    <div className="space-y-3">
      {items.map((it) => (
        <Card key={it.id} className="p-4 space-y-3">
          <div className="flex flex-wrap items-center gap-2 justify-between">
            <div className="flex items-center gap-2">
              <Badge variant={it.priority < 50 ? "destructive" : it.priority < 100 ? "default" : "outline"}>
                P{it.priority}
              </Badge>
              <Badge variant="secondary">{SOURCE_LABELS[it.source_kind] ?? it.source_kind}</Badge>
              {it.source_ref && <code className="text-xs text-muted-foreground">{it.source_ref}</code>}
            </div>
            <p className="text-xs text-muted-foreground">
              {new Date(it.created_at).toLocaleString()}
            </p>
          </div>
          {it.subject_user_id && (
            <p className="text-xs">
              Subject user: <code className="text-muted-foreground">{it.subject_user_id}</code>
            </p>
          )}
          {it.notes && (
            <p className="text-sm text-muted-foreground italic">"{it.notes}"</p>
          )}
          {status === "pending" || status === "in_review" ? (
            <>
              <Textarea
                placeholder="Resolution note (optional)"
                rows={2}
                value={notes[it.id] ?? ""}
                onChange={(e) => setNotes({ ...notes, [it.id]: e.target.value })}
              />
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" disabled={!!busy} onClick={() => act(it.id, "resolved_keep")}>
                  <Eye className="h-4 w-4 mr-1" /> Keep
                </Button>
                <Button size="sm" variant="outline" disabled={!!busy} onClick={() => act(it.id, "resolved_warn")}>
                  Warn
                </Button>
                <Button size="sm" variant="destructive" disabled={!!busy} onClick={() => act(it.id, "resolved_remove")}>
                  <ShieldX className="h-4 w-4 mr-1" /> Remove
                </Button>
                <Button size="sm" variant="destructive" disabled={!!busy} onClick={() => act(it.id, "resolved_ban")}>
                  Ban
                </Button>
                <Button size="sm" variant="ghost" disabled={!!busy} onClick={() => act(it.id, "escalated")}>
                  <ShieldAlert className="h-4 w-4 mr-1" /> Escalate
                </Button>
              </div>
            </>
          ) : (
            it.resolution_note && (
              <p className="text-xs text-muted-foreground border-t border-border pt-2">
                Resolution: {it.resolution_note}
              </p>
            )
          )}
        </Card>
      ))}
    </div>
  );
};

const ReviewQueuePanel = () => (
  <Tabs defaultValue="pending" className="space-y-4">
    <TabsList>
      <TabsTrigger value="pending">Pending</TabsTrigger>
      <TabsTrigger value="in_review">In review</TabsTrigger>
      <TabsTrigger value="escalated">Escalated</TabsTrigger>
      <TabsTrigger value="resolved_remove">Removed</TabsTrigger>
    </TabsList>
    <TabsContent value="pending"><Queue status="pending" /></TabsContent>
    <TabsContent value="in_review"><Queue status="in_review" /></TabsContent>
    <TabsContent value="escalated"><Queue status="escalated" /></TabsContent>
    <TabsContent value="resolved_remove"><Queue status="resolved_remove" /></TabsContent>
  </Tabs>
);

export default ReviewQueuePanel;
